import React, { useEffect, useMemo, useState } from "react";
import MuiAlert from "@mui/material/Alert";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Stack, Typography, IconButton, Divider,
  Chip, MenuItem, Select, FormControl, InputLabel, Box,
  FormControlLabel, Checkbox
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import DeleteIcon from "@mui/icons-material/Delete";
import { splitEligiblePassengers } from "../../utils/transportEligibility.js";

function toAirportOption(a) {
  const code = (a?.code || "").trim();
  const name = (a?.name || "").trim();
  const label = name && code ? `${name} (${code})` : (name || code || "");
  return { kind: "AIRPORT", key: `AIRPORT:${code}`, label, location: name || code, airportCode: code };
}

function toCampusOption(c) {
  const id = c?.id;
  const label = c?.campusName || c?.name || `Campus ${id}`;
  return { kind: "CAMPUS", key: `CAMPUS:${id}`, label, location: label, campusId: id };
}

function normaliseTime(val) {
  if (!val) return "";
  const s = String(val);
  if (/^\d{2}:\d{2}:\d{2}$/.test(s)) return s.slice(0, 5);
  if (/^\d{2}:\d{2}$/.test(s)) return s;
  return "";
}

// Order-sensitive signature of a route, used to detect whether a confirmed
// group's stops have actually changed (which must force an unconfirm).
function stopsSignature(stops) {
  return JSON.stringify(
    (Array.isArray(stops) ? stops : [])
      .slice()
      .sort((a, b) => (a.stopOrder ?? 0) - (b.stopOrder ?? 0))
      .map((s, i) => ({
        order: i + 1,
        location: String(s.location || "").trim(),
        campusId: String(s.campusId ?? ""),
        plannedTime: normaliseTime(s.plannedTime) || "",
        stopType: String(s.stopType || "").toUpperCase(),
      }))
  );
}

export default function AddTransportGroupDialog({
  open,
  onClose,
  onCreated,

  mode = "add",
  initialGroup = null,
  onUpdated,

  campuses = [],
  servedAirports = [],

  currentPassengers = [],         // rows assigned to this bus
  eligibilityCtx = null,          // from makeEligibilityContext(...)
  onUnassignMismatched = null,    // async (mismatchedRows) => void
}) {
  const [groupName, setGroupName] = useState("");
  const [capacity, setCapacity] = useState(15);
  const [provider, setProvider] = useState("");
  const [notes, setNotes] = useState("");
  const [stops, setStops] = useState([]);
  const [mismatchPreview, setMismatchPreview] = useState([]);
  const TRANSFER_JOURNEY_TYPES = ["ARRIVALS", "DEPARTURES"];
  const [journeyType, setJourneyType] = useState("ARRIVALS");

  // Controlled stop picker so it clears after each pick (otherwise the last
  // selection lingers in the field and has to be cleared before adding another).
  const [stopPickerValue, setStopPickerValue] = useState(null);
  const [stopPickerInput, setStopPickerInput] = useState("");

  // Confirmation (edit mode only) — mirrors the Transport board's bus dialog.
  const [contactName, setContactName] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [confirmNow, setConfirmNow] = useState(false);
  const [unconfirmNow, setUnconfirmNow] = useState(false);

  const isConfirmed =
    mode === "edit" &&
    (String(initialGroup?.status || "").trim().toLowerCase() === "confirmed" ||
      Boolean(initialGroup?.confirmedAt));

  const reset = () => {
    setGroupName("");
    setCapacity(15);
    setProvider("");
    setNotes("");
    setStops([]);
    setJourneyType("ARRIVALS");
    setStopPickerValue(null);
    setStopPickerInput("");
    setContactName("");
    setContactNumber("");
    setConfirmNow(false);
    setUnconfirmNow(false);
  };

  const options = useMemo(() => {
    const airportOpts = (servedAirports || []).map(toAirportOption).filter((o) => o.label);
    const campusOpts = (campuses || []).map(toCampusOption).filter((o) => o.label);
    return [
      ...airportOpts.map((o) => ({ ...o, group: "Airports" })),
      ...campusOpts.map((o) => ({ ...o, group: "Campuses" })),
    ];
  }, [servedAirports, campuses]);

  useEffect(() => {
    if (!open) return;

    if (mode === "edit" && initialGroup) {
      setGroupName(initialGroup.groupName || "");
      setCapacity(initialGroup.capacity ?? 15);
      setProvider(initialGroup.provider || "");
      setNotes(initialGroup.notes || "");
      setJourneyType((initialGroup.journeyType || "ARRIVALS").toUpperCase());
      setContactName(initialGroup.contactName || "");
      setContactNumber(initialGroup.contactNumber || "");
      setConfirmNow(false);
      setUnconfirmNow(false);
      setStops(
        Array.isArray(initialGroup.stops)
          ? initialGroup.stops.map((s) => ({
              stopOrder: s.stopOrder,
              location: s.location,
              campusId: s.campusId ?? null,
              plannedTime: normaliseTime(s.plannedTime),
              stopType: s.stopType,
            }))
          : []
      );
    } else {
      reset();
      setJourneyType("ARRIVALS");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, initialGroup]);

  const findMismatched = (busLike) => {
    if (!eligibilityCtx) return [];
    if (!currentPassengers || currentPassengers.length === 0) return [];
    const { ineligible } = splitEligiblePassengers(currentPassengers, busLike, eligibilityCtx);
    return ineligible;
  };

  useEffect(() => {
    if (!open) return;
    if (mode !== "edit") return;
    if (!initialGroup?.id) return;

    const cleanedStops = (stops || []).map((s, i) => ({
      stopOrder: i + 1,
      location: s.location,
      campusId: s.campusId ?? null,
      plannedTime: normaliseTime(s.plannedTime),
      stopType: s.stopType || (i === 0 ? "PICKUP" : "DROPOFF"),
    }));

    const busAfter = { ...initialGroup, stops: cleanedStops };
    const mismatched = findMismatched(busAfter);

    setMismatchPreview(mismatched);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, initialGroup?.id, stops, eligibilityCtx, currentPassengers]);

  const addStopFromValue = (value) => {
    if (!value) return;

    if (typeof value === "string") {
      const label = value.trim();
      if (!label) return;
      setStops((prev) => {
        const nextOrder = prev.length + 1;
        return [
          ...prev,
          {
            stopOrder: nextOrder,
            location: label,
            campusId: null,
            plannedTime: "",
            stopType: nextOrder === 1 ? "PICKUP" : "DROPOFF",
          },
        ];
      });
      return;
    }

    const location = value.location || value.label;
    const campusId = value.kind === "CAMPUS" ? value.campusId : null;

    setStops((prev) => {
      const nextOrder = prev.length + 1;
      return [
        ...prev,
        {
          stopOrder: nextOrder,
          location,
          campusId: campusId ?? null,
          plannedTime: "",
          stopType: nextOrder === 1 ? "PICKUP" : "DROPOFF",
        },
      ];
    });
  };

  const removeStop = (idx) => {
    setStops((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      return next.map((s, i) => ({
        ...s,
        stopOrder: i + 1,
        stopType: i === 0 ? (s.stopType || "PICKUP") : (s.stopType || "DROPOFF"),
      }));
    });
  };

  const updateStop = (idx, patch) => {
    setStops((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };

  // Effective stop type mirrors the per-row Select default (first stop PICKUP,
  // the rest DROPOFF) so the check matches what the user actually sees.
  const effectiveStopType = (s, idx) =>
    String(s.stopType || (idx === 0 ? "PICKUP" : "DROPOFF")).toUpperCase();

  const hasPickup = stops.some((s, i) => effectiveStopType(s, i) === "PICKUP");
  const hasDropoff = stops.some((s, i) => effectiveStopType(s, i) === "DROPOFF");

  // A stop counts as one of our campuses only if it carries a campusId, which
  // is set when the stop is picked from the Campuses list (not free-typed).
  // That campusId is what Transport Plan uses to attribute the journey to a
  // campus, so requiring it here stops "orphaned" (Unassigned) groups.
  const hasCampusStop = stops.some((s) => s.campusId != null);

  const canSave =
    groupName.trim().length > 0 &&
    Number(capacity) > 0 &&
    TRANSFER_JOURNEY_TYPES.includes(String(journeyType || "").toUpperCase()) &&
    hasPickup &&
    hasDropoff &&
    hasCampusStop;

  // Confirmation gating (edit mode) — same model as the Transport board.
  const canConfirm =
    String(contactName || "").trim() !== "" && String(contactNumber || "").trim() !== "";

  // A confirmed group whose capacity or route actually changed MUST unconfirm —
  // enforced from the data so it can't be bypassed by unticking.
  const protectedChanged =
    isConfirmed &&
    (Number(capacity) !== Number(initialGroup?.capacity) ||
      stopsSignature(stops) !== stopsSignature(initialGroup?.stops));

  const unconfirmEffective = unconfirmNow || protectedChanged;
  // While confirmed, capacity/route are locked until the group is being unconfirmed.
  const routeLocked = isConfirmed && !unconfirmEffective;

  const handleClose = () => {
    onClose?.();
    if (mode !== "edit") reset();
  };

  const handleSave = async () => {
    if (!canSave) return;

    const cleanedStops = (stops || []).map((s, i) => ({
      stopOrder: i + 1,
      location: s.location,
      campusId: s.campusId ?? null,
      plannedTime: normaliseTime(s.plannedTime),
      stopType: s.stopType || (i === 0 ? "PICKUP" : "DROPOFF"),
    }));

    const payload = {
      groupName: groupName.trim(),
      capacity: Number(capacity),
      provider: provider.trim() || null,
      notes: notes || "",
      journeyType,
      stops: cleanedStops,
    };

    if (mode === "edit" && initialGroup?.id) {
      const editPayload = {
        ...payload,
        contactName: contactName.trim(),
        contactNumber: contactNumber.trim(),
        confirm: Boolean(confirmNow),
        unconfirm: unconfirmEffective,
      };

      // 1️⃣ Save route changes (+ contact / confirm / unconfirm)
      await Promise.resolve(onUpdated?.(initialGroup.id, editPayload));

      // 2️⃣ If mismatched passengers exist, auto-unassign
      if (mismatchPreview.length > 0 && typeof onUnassignMismatched === "function") {
        await Promise.resolve(
          onUnassignMismatched(mismatchPreview.map((x) => x.passenger))
        );
      }

      onClose?.();
      return;
    }

    onCreated?.(payload);
    reset();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>{mode === "edit" ? "Edit Transport Group" : "Add Transport Group"}</DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Group name"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              fullWidth
            />
            <TextField
              label="Capacity"
              type="number"
              value={capacity}
              onChange={(e) => setCapacity(e.target.value)}
              disabled={routeLocked}
              helperText={routeLocked ? "Unconfirm to change capacity" : ""}
              sx={{ width: { xs: "100%", sm: 200 } }}
              inputProps={{ min: 1 }}
            />
          </Stack>

          <FormControl fullWidth>
            <InputLabel id="journey-type-label">Journey type</InputLabel>
            <Select
              labelId="journey-type-label"
              label="Journey type"
              value={journeyType}
              onChange={(e) => setJourneyType(e.target.value)}
            >
              {TRANSFER_JOURNEY_TYPES.map((t) => (
                <MenuItem key={t} value={t}>{t}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Provider"
            value={provider}
            onChange={(e) => setProvider(e.target.value)}
            fullWidth
          />

          <TextField
            label="Notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />

          {mode === "edit" && (
            <>
              <Divider />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  label="Contact name"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Contact number"
                  value={contactNumber}
                  onChange={(e) => setContactNumber(e.target.value)}
                  fullWidth
                />
              </Stack>

              {!isConfirmed && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={confirmNow}
                      disabled={!canConfirm}
                      onChange={(e) => setConfirmNow(e.target.checked)}
                    />
                  }
                  label={
                    <Stack spacing={0}>
                      <Typography variant="body2">Confirm this bus</Typography>
                      <Typography variant="caption" sx={{ opacity: 0.7 }}>
                        {canConfirm
                          ? "You will be recorded as the person who confirmed this group."
                          : "Add contact name and number to enable confirmation"}
                      </Typography>
                    </Stack>
                  }
                />
              )}

              {isConfirmed && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={unconfirmEffective}
                      disabled={protectedChanged}
                      onChange={(e) => setUnconfirmNow(e.target.checked)}
                    />
                  }
                  label={
                    <Stack spacing={0}>
                      <Typography variant="body2">Unconfirm this bus</Typography>
                      <Typography variant="caption" sx={{ opacity: 0.7 }}>
                        {protectedChanged
                          ? "Capacity/route changed — saving will unconfirm this group. Re-confirm afterwards."
                          : "Reverts this group to unconfirmed so it can be edited"}
                      </Typography>
                    </Stack>
                  }
                />
              )}
            </>
          )}

          {mode === "edit" && mismatchPreview.length > 0 && (
            <MuiAlert severity="warning" variant="outlined">
              This change will make this bus ineligible for <strong>{mismatchPreview.length}</strong> passenger(s).
              <Box sx={{ mt: 1 }}>
                {mismatchPreview.slice(0, 5).map((x, idx) => (
                  <div key={idx}>
                    • {x.passenger?.name || x.passenger?.studentId || "Unknown"} — {x.reason}
                  </div>
                ))}
                
                {mismatchPreview.length > 5 && (
                  <div>…and {mismatchPreview.length - 5} more</div>
                )}
              </Box>
            </MuiAlert>
          )}
          
          <Divider />
          <Stack direction="row" spacing={1} alignItems="baseline">
            <Typography variant="subtitle1">Stops</Typography>
            {routeLocked && (
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                Unconfirm to change the route
              </Typography>
            )}
          </Stack>

          <Autocomplete
            options={options}
            groupBy={(o) => o.group}
            getOptionLabel={(o) => (typeof o === "string" ? o : (o.label || ""))}
            freeSolo
            disabled={routeLocked}
            value={stopPickerValue}
            onChange={(_, value) => {
              addStopFromValue(value);
              setStopPickerValue(null);
              setStopPickerInput("");
            }}
            inputValue={stopPickerInput}
            onInputChange={(_, newInputValue, reason) => {
              // Ignore the "reset" that fires on selection — onChange already
              // clears the field. Track typing and the clear (X) button.
              if (reason !== "reset") setStopPickerInput(newInputValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Add stop (airport, campus, or type an address)"
                placeholder="e.g. Edinburgh Airport, Loretto, Premier Inn Haymarket..."
              />
            )}
          />

          {stops.length === 0 ? (
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              No stops added yet.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {stops.map((s, idx) => (
                <Stack
                  key={`${s.stopOrder}-${s.location}-${idx}`}
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  alignItems={{ sm: "center" }}
                >
                  <Chip label={`#${idx + 1}`} size="small" sx={{ width: { xs: "fit-content", sm: 60 } }} />

                  <TextField
                    label="Location"
                    value={s.location || ""}
                    onChange={(e) => updateStop(idx, { location: e.target.value })}
                    disabled={routeLocked}
                    fullWidth
                  />

                  <TextField
                    label="Planned time (HH:mm)"
                    value={s.plannedTime || ""}
                    onChange={(e) => updateStop(idx, { plannedTime: e.target.value })}
                    onBlur={() => updateStop(idx, { plannedTime: normaliseTime(s.plannedTime) })}
                    disabled={routeLocked}
                    sx={{ width: { xs: "100%", sm: 170 } }}
                    placeholder="09:30"
                  />

                  <FormControl sx={{ width: { xs: "100%", sm: 160 } }} disabled={routeLocked}>
                    <InputLabel id={`stoptype-${idx}`}>Type</InputLabel>
                    <Select
                      labelId={`stoptype-${idx}`}
                      label="Type"
                      value={s.stopType || (idx === 0 ? "PICKUP" : "DROPOFF")}
                      onChange={(e) => updateStop(idx, { stopType: e.target.value })}
                    >
                      <MenuItem value="PICKUP">PICKUP</MenuItem>
                      <MenuItem value="DROPOFF">DROPOFF</MenuItem>
                    </Select>
                  </FormControl>

                  <IconButton onClick={() => removeStop(idx)} aria-label="Remove stop" disabled={routeLocked}>
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
          )}

          {(!hasPickup || !hasDropoff) && (
            <Typography variant="caption" color="warning.main">
              Add at least one PICKUP and one DROPOFF stop to save.
            </Typography>
          )}
          {!hasCampusStop && (
            <Typography variant="caption" color="warning.main">
              Add at least one stop at one of your campuses (choose it from the Campuses list).
            </Typography>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button variant="contained" disabled={!canSave} onClick={handleSave}>
          {mode === "edit"
          ? mismatchPreview.length > 0
          ? `Save & Unassign ${mismatchPreview.length}`
          : "Save"
          : "Create"}
        </Button>
        
      </DialogActions>
    </Dialog>
  );
}